import { ElementType, FC, ReactNode } from "react";

import { PunchupTag } from "@punchup/ui-common/punchup/primitives/display/PunchupTag";
import { cx } from "../../../utils/cx";
import styles from "./PunchupHeroCard.module.css";

export interface PunchupHeroCardProps {
  /**
   * Surface tone. `ink` — the constitution's `card-hero` (Stage-Black panel,
   * warm-white text, orange spotlight); `paper` — the light-theme sibling
   * (white→cream wash with a warm sunrise glow, ink text) for surfaces where a
   * dark panel would fight the light page. Recorded divergence — see CLAUDE.md.
   */
  tone?: "ink" | "paper";
  /**
   * Optional eyebrow chip above the headline (e.g. "The Comedy Platform"),
   * rendered as a display-mode `PunchupTag` in the brand orange.
   */
  eyebrow?: string;
  /**
   * The display headline. Wrap the brand word in `PunchupHeroAccent` to give it
   * the orange spotlight: `Welcome to <PunchupHeroAccent>Punchup</PunchupHeroAccent>`.
   */
  headline: ReactNode;
  /**
   * Heading element for the headline. Defaults to `h1` — the hero usually IS the
   * page's top heading (a logged-out landing moment). Pass `h2`/`div` when the
   * page already has an h1.
   */
  headingAs?: ElementType;
  /** Supporting value-prop copy under the headline. */
  body?: ReactNode;
  /**
   * CTA row — pass `PunchupButton size="lg"` (with `asChild` + a link for
   * navigation) so the hero carries the surface's single doorway action.
   */
  actions?: ReactNode;
  /**
   * Purely decorative layer (floating accents, artwork). Rendered absolutely
   * over the card, `aria-hidden`, pointer-events disabled — it can never block
   * the CTA or reach screen readers.
   */
  decorative?: ReactNode;
  /**
   * Decorative media column (e.g. a face grid of real talent), laid out
   * side-by-side with the copy at md+ and hidden on mobile. In-flow — unlike
   * `decorative` it reserves its own space so it can never overlap the
   * headline. `aria-hidden`: it is presentation, not content.
   */
  media?: ReactNode;
  className?: string;
}

/**
 * PunchupHeroCard — the constitution's `card-hero` surface: a Stage-Black
 * (ink-900) panel with warm-white text, hero radius, and 32px padding. The
 * brand-moment card for logged-out landing surfaces (home feed hero).
 */
export const PunchupHeroCard: FC<PunchupHeroCardProps> = ({
  tone = "ink",
  eyebrow,
  headline,
  headingAs: Heading = "h1",
  body,
  actions,
  decorative,
  media,
  className,
}) => (
  <section
    className={cx(styles.hero, tone === "paper" && styles.tonePaper, className)}
  >
    {decorative && (
      <div className={styles.decorative} aria-hidden="true">
        {decorative}
      </div>
    )}
    <div className={styles.inner}>
      <div className={styles.content}>
        {eyebrow && (
          <div className={styles.eyebrow}>
            <PunchupTag label={eyebrow} />
          </div>
        )}
        <Heading className={styles.headline}>{headline}</Heading>
        {body && (
          <div
            className={cx(styles.body, tone === "paper" && styles.bodyPaper)}
          >
            {body}
          </div>
        )}
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {media && (
        <div className={styles.media} aria-hidden="true">
          {media}
        </div>
      )}
    </div>
  </section>
);

/**
 * The orange-spotlight span for the brand word inside the hero headline —
 * `Welcome to <PunchupHeroAccent>Punchup</PunchupHeroAccent>`.
 */
export const PunchupHeroAccent: FC<{ children: ReactNode }> = ({
  children,
}) => <span className={styles.accent}>{children}</span>;
